import { useState, useEffect } from 'react';
import { Search, Book, ChevronDown, ChevronUp } from 'lucide-react';
import { useAccessibleKnowledgeBases, KnowledgeBase } from '@/hooks/useKnowledgeBases';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';

interface KBConnectionPanelProps {
  connectedKBs: string[];
  onConnect: (kbId: string) => void;
  onDisconnect: (kbId: string) => void;
  canConnectMore: boolean;
  recentlyUsed: string[];
  maxConnectedKBs: number;
}

export const KBConnectionPanel = ({
  connectedKBs,
  onConnect,
  onDisconnect,
  canConnectMore,
  recentlyUsed,
  maxConnectedKBs
}: KBConnectionPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userId, setUserId] = useState<string | undefined>();
  
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id);
    });
  }, []);

  const { data: allKBs, isLoading } = useAccessibleKnowledgeBases(userId);

  const filteredKBs = allKBs?.filter(kb =>
    kb.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kb.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const recentKBs = allKBs?.filter(kb => recentlyUsed.includes(kb.id));

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-t pt-2">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between">
          <span className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            Connect Knowledge Base ({connectedKBs.length}/{maxConnectedKBs})
          </span>
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-2 pt-2">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search knowledge bases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>

        <ScrollArea className="h-[200px]">
          {/* Recently Used Section */}
          {recentKBs && recentKBs.length > 0 && !searchQuery && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Recently Used</p>
              {recentKBs.map(kb => (
                <KBListItem
                  key={kb.id}
                  kb={kb}
                  isConnected={connectedKBs.includes(kb.id)}
                  onToggle={() => 
                    connectedKBs.includes(kb.id) 
                      ? onDisconnect(kb.id) 
                      : onConnect(kb.id)
                  }
                  canConnect={canConnectMore}
                />
              ))}
            </div>
          )}

          {/* All KBs */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              {searchQuery ? 'Search Results' : 'All Knowledge Bases'}
            </p>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : filteredKBs && filteredKBs.length > 0 ? (
              filteredKBs.map(kb => (
                <KBListItem
                  key={kb.id}
                  kb={kb}
                  isConnected={connectedKBs.includes(kb.id)}
                  onToggle={() => 
                    connectedKBs.includes(kb.id) 
                      ? onDisconnect(kb.id) 
                      : onConnect(kb.id)
                  }
                  canConnect={canConnectMore}
                />
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No knowledge bases found</p>
            )}
          </div>
        </ScrollArea>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Helper component
interface KBListItemProps {
  kb: KnowledgeBase;
  isConnected: boolean;
  onToggle: () => void;
  canConnect: boolean;
}

const KBListItem = ({ kb, isConnected, onToggle, canConnect }: KBListItemProps) => (
  <div className="flex items-start gap-2 p-2 hover:bg-muted rounded-md">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">{kb.title}</p>
      <p className="text-xs text-muted-foreground truncate">
        {kb.description || 'No description'}
      </p>
    </div>
    <Button
      size="sm"
      variant={isConnected ? "secondary" : "outline"}
      onClick={onToggle}
      disabled={!isConnected && !canConnect}
    >
      {isConnected ? 'Disconnect' : 'Connect'}
    </Button>
  </div>
);
